import { useEffect, useMemo, useState } from 'react';
import { FunnelInsightsApiClient } from '../api/insightsClient';
import type {
  DroppedAudienceData,
  FunnelInsightWarning,
} from '../types';

/**
 * I-A — on-demand «who dropped at this step» fetch for the flow-loss stub
 * click (mirror of useFunnelEdgeDrilldown; dropScreenId instead of
 * canvasTransitionId). No cache: the surface opens occasionally and must
 * reflect the live warehouse (P-3).
 */

interface UseFlowLossDrilldownInput {
  projectId: string;
  onboardingId: string;
  versionId: string;
  dropScreenId?: string;
  enabled: boolean;
  from?: string;
  to?: string;
}

interface UseFlowLossDrilldownResult {
  data: DroppedAudienceData | null;
  loading: boolean;
  error: string | null;
  warnings: FunnelInsightWarning[];
}

export function useFlowLossDrilldown(input: UseFlowLossDrilldownInput): UseFlowLossDrilldownResult {
  const [data, setData] = useState<DroppedAudienceData | null>(null);
  const [warnings, setWarnings] = useState<FunnelInsightWarning[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(() => new FunnelInsightsApiClient(), []);
  const canLoad = input.enabled && Boolean(
    input.projectId &&
    input.onboardingId &&
    input.versionId &&
    input.dropScreenId,
  );

  useEffect(() => {
    if (!canLoad || !input.dropScreenId) {
      setData(null);
      setWarnings([]);
      setLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    client.getDropoffEdge({
      projectId: input.projectId,
      onboardingId: input.onboardingId,
      versionId: input.versionId,
      dropScreenId: input.dropScreenId,
      from: input.from,
      to: input.to,
      signal: controller.signal,
    }).then((response) => {
      setData(response.data.droppedAudience ?? null);
      setWarnings(response.warnings ?? []);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      setData(null);
      setWarnings([]);
      setError(err instanceof Error ? err.message : 'Failed to load drop-off audience');
    }).finally(() => {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    });

    return () => controller.abort();
  }, [
    canLoad,
    client,
    input.projectId,
    input.onboardingId,
    input.versionId,
    input.dropScreenId,
    input.from,
    input.to,
  ]);

  return { data, loading, error, warnings };
}
